import { Button } from "@blueskydataplatform/shared-ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@blueskydataplatform/shared-ui/components/ui/card";
import { Input } from "@blueskydataplatform/shared-ui/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Cookies from "js-cookie";
import { useParams } from "react-router";

import { UILink } from "@/components/ui-link";
import { paths } from "@/config/paths";
import { usePreserveQuery } from "@/hooks/use-preserve-query";
import { Checkbox } from "@blueskydataplatform/shared-ui/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@blueskydataplatform/shared-ui/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@blueskydataplatform/shared-ui/components/ui/select";
import { Separator } from "@blueskydataplatform/shared-ui/components/ui/separator";
import { useToast } from "@blueskydataplatform/shared-ui/hooks/use-toast";
import { useNavigate } from "react-router";
import { useSignUp, type SignUpInput } from "../api/sign-up";
import Logo from "@/components/logo";
import useQueryParams, { SIGN_IN_QUERY } from "@/hooks/use-query-params";
import { USE_CASES } from "@blueskydataplatform/shared-ui/config/use-cases";
import { COMPANY_SIZES } from "@blueskydataplatform/shared-ui/config/company-sizes";
import { ROLES } from "@blueskydataplatform/shared-ui/config/roles";
import { useMcpProvider } from "@/providers/mcp-providers-context";

const baseSchema = z.object({
  email: z.string().email({ message: "Invalid email" }),
  fullName: z.string().min(2, { message: "Full name is required" }),
  useCase: z.string().optional(),
  orgType: z.enum(["business", "individual"]),
  role: z.string(),
  company: z.string().optional(),
  employeesCount: z.string().optional(),
  password: z.string().min(8, { message: "At least 8 characters" }),
  inviteCode: z.string().optional(),
  termsAccepted: z.boolean().refine(v => v, { message: "You must accept the Terms of Service" }),
  isFromMCP: z.boolean().optional(),
  provider: z.string().optional(),
});

const registerSchema = baseSchema.superRefine((data, ctx) => {
  if (!data.inviteCode) {
    if (data.orgType === "business") {
      const validRoles = ROLES.find(role => role.label === "Commercial")?.options.map(role => role.value);
      if (!validRoles?.includes(data.role as any)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["role"], message: "Select a valid role" });
      }
      if (!data.company) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["company"], message: "Company is required" });
      }
      if (!data.employeesCount) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["employeesCount"], message: "Please select a size" });
      }
    } else {
      const validRoles = ROLES.find(role => role.label === "Individual")?.options.map(role => role.value);
      if (!validRoles?.includes(data.role as any)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["role"], message: "Select a valid role" });
      }
    }
  }
});

type FormValues = z.infer<typeof registerSchema>;

const SignUpForm = () => {
  const { inviteCode: inviteCodeQuery, referrerCode, partner, email, userType: userTypeQuery } = useQueryParams();
  const { inviteCode: inviteCodeParam } = useParams<{ inviteCode?: string }>();
  const inviteCode = inviteCodeParam || inviteCodeQuery || null;
  const initialUserTypeByQuery = userTypeQuery === "commercial" ? "business" : "individual";
  const initialUserType = userTypeQuery ? initialUserTypeByQuery : "business";
  const { isFromMCP, state: mcpState } = useQueryParams();
  const { provider } = useMcpProvider();
  const form = useForm<FormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      inviteCode: inviteCode || undefined,
      email: email || "",
      fullName: "",
      useCase: "",
      orgType: initialUserType,
      role: initialUserType === "business" ? "company" : "individual-investor",
      company: "",
      employeesCount: "",
      password: "",
      termsAccepted: false,
      isFromMCP,
      provider: provider?.id,
    },
  });

  const orgType = form.watch("orgType");
  const { toast } = useToast();
  const navigate = useNavigate();

  const preserveQuery = usePreserveQuery();
  const registerMutation = useSignUp({
    mutationConfig: {
      onSuccess: () => {
        const url = preserveQuery(paths.finishRegistration.getHref());
        navigate(url);
      },
      onError: (err: any) => {
        if (err.response && err.response.status === 400) {
          const error = err.response.data;
          // The server does not say which rule refused the address, so show its message
          // verbatim and put it on the field the user has to change.
          if (error.code === "EMAIL_NOT_ALLOWED") {
            form.setError("email", { type: "manual", message: error.error });
            form.setFocus("email");
            toast.error(error.error);
            return;
          }
          if (error.field === "email" && error.code.indexOf("EXISTS") > -1) {
            const message = "An account with this email already exists. Please use a different email address.";
            form.setError("email", { type: "manual", message });
            form.setFocus("email");
            toast.error(message);
            return;
          }
          if (error.field === "password") {
            const message = "Please use a less common password.";
            form.setError("password", { type: "manual", message });
            form.setFocus("password");
            toast.error(message);
            return;
          }
          if (error.code === "BAD_INVITE") {
            const message = "Your invitation is not valid. Please contact the account administrator.";
            form.setError("root", { type: "manual", message });
            toast.error(message);
            return;
          }
          if (error.code === "ER_DUP_ENTRY") {
            const message = "An account with this email already exists.";
            form.setError("email", { type: "manual", message });
            form.setFocus("email");
            toast.error(message);
            return;
          }
        }
        // Fallback error message for any unhandled errors
        const message = "An error occurred while creating your account. Please try again.";
        form.setError("root", { type: "manual", message });
        toast.error(message);
      },
    },
  });

  const onSubmit = (values: FormValues) => {
    const cookieReferrerCode = Cookies.get("cloud-refid");
    const cookiePartner = Cookies.get("cloud-partner");

    const payload: SignUpInput = {
      inviteCode,
      referrerCode: referrerCode || cookieReferrerCode || null,
      partner: partner || cookiePartner || null,
      email: values.email,
      fullName: values.fullName,
      useCase: values.useCase,
      userType: values.role,
      orgType: values.orgType,
      password: values.password,
      hasCheckedVal: false, // honeypot field
      companyName: values.company || values.fullName,
      numEmployees: values.orgType === "business" ? values.employeesCount : undefined,
      isFromMCP,
      provider: provider?.id,
      state: mcpState,
    };
    registerMutation.mutate({ data: payload });
  };

  const actualRoles = orgType === "business" ? ROLES[1].options : ROLES[0].options;
  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-xl">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-2">
            <Logo />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Create your account</CardTitle>
          <CardDescription className="text-center">Sign up to get started</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="Enter email" {...field} disabled={!!inviteCode || !!partner} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter full name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!inviteCode && (
                  <FormField
                    control={form.control}
                    name="useCase"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>What are you using the platform for? *</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger aria-label="Select what you're using the platform for">
                              <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {USE_CASES.map(useCase => (
                              <SelectItem key={useCase.value} value={useCase.value}>
                                {useCase.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <Separator />

              <div className="space-y-4">
                {!inviteCode && (
                  <>
                    <FormField
                      control={form.control}
                      name="orgType"
                      render={() => (
                        <FormItem>
                          <FormLabel>Type of organization *</FormLabel>
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              type="button"
                              variant={orgType === "business" ? "default" : "outline"}
                              onClick={() => {
                                form.setValue("orgType", "business", { shouldValidate: true, shouldDirty: true });
                                form.setValue("role", "company", { shouldValidate: true, shouldDirty: true });
                              }}
                            >
                              Commercial
                            </Button>
                            <Button
                              type="button"
                              variant={orgType === "individual" ? "default" : "outline"}
                              onClick={() => {
                                form.setValue("orgType", "individual", { shouldValidate: true, shouldDirty: true });
                                form.setValue("role", "individual-investor", {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              }}
                            >
                              Non-commercial
                            </Button>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="role"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Select your role *</FormLabel>
                          <div className="grid grid-cols-1 gap-2">
                            {actualRoles.map(role => (
                              <Button
                                key={role.value}
                                type="button"
                                variant={field.value === role.value ? "default" : "outline"}
                                onClick={() => field.onChange(role.value)}
                              >
                                {role.label}
                              </Button>
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {orgType === "business" && !inviteCode && (
                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company *</FormLabel>
                            <FormControl>
                              <Input placeholder="Your company name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </>
                )}

                {orgType === "business" && !inviteCode && (
                  <FormField
                    control={form.control}
                    name="employeesCount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Select number of employees *</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select size" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {COMPANY_SIZES.map(size => (
                              <SelectItem key={size.value} value={size.value}>
                                {size.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password *</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="Create a password" {...field} />
                      </FormControl>
                      <p className="text-xs text-gray-500">Must have at least eight characters</p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="termsAccepted"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-start space-x-3">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={v => field.onChange(Boolean(v))} />
                      </FormControl>
                      <FormLabel className="font-normal">
                        I agree to the Blue-Sky Data Platform{" "}
                        <a href="https://vianexus.com/terms" target="_blank" className="underline" rel="noreferrer">
                          Terms of Service
                        </a>
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.formState.errors.root && (
                <div className="rounded bg-red-50 p-4 border border-red-200">
                  <p className="text-sm text-red-800">{form.formState.errors.root.message}</p>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={registerMutation.isPending}>
                {registerMutation.isPending ? "Creating account..." : "Create account"}
              </Button>
              <div className="text-center text-sm">
                <UILink to={preserveQuery(paths.home.getHref(), SIGN_IN_QUERY)} className="text-sm">
                  Return to sign in
                </UILink>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignUpForm;
